/**
 * WebStorage — IStorage impl backed by `localStorage`.
 *
 * This is the **only** file in `src/` allowed to reference `localStorage`
 * directly. The Phase 1a service-layer invariant (`no-restricted-globals`)
 * forbids the global elsewhere; the allow-zone covers `src/services/storage/**`.
 *
 * On SSR / build-time the module must not crash. We check `typeof window`
 * and fall back to an in-memory map.
 */

import type { IStorage } from '../interfaces/IStorage';

// In-memory fallback for SSR + tests that import this module without a DOM.
const fallback = new Map<string, string>();

const hasLocalStorage = (): boolean =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

export const webStorage: IStorage = {
  get(key: string): string | null {
    if (hasLocalStorage()) {
      return window.localStorage.getItem(key);
    }
    return fallback.get(key) ?? null;
  },

  set(key: string, value: string): void {
    if (hasLocalStorage()) {
      window.localStorage.setItem(key, value);
      return;
    }
    fallback.set(key, value);
  },

  remove(key: string): void {
    if (hasLocalStorage()) {
      window.localStorage.removeItem(key);
      return;
    }
    fallback.delete(key);
  },
};
